import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import moment from 'moment';

const API_URL = environment.prismaApiUrl +'/api/dispo/stats';

@Injectable({
  providedIn: 'root'
})
export class StatsService {
  currentObject: any;
  refreshed: any;
  isNew: Boolean = false;

  constructor(private http: HttpClient) { }

  getStats(params?: { history_limit?: number; history_offset?: number }): Observable<any> {
    const httpParams: { [key: string]: string } = {};
    if (params?.history_limit != null) httpParams['history_limit'] = String(params.history_limit);
    if (params?.history_offset != null) httpParams['history_offset'] = String(params.history_offset);
    return this.http.get(API_URL, Object.keys(httpParams).length ? { params: httpParams } : {});
  }

  /** Wochenübersicht aller Teams: Auslastung, Aufträge, Tour-km/Dauer, Ampel. */
  getTeamCalendarOverview(params: { start: string; end: string }): Observable<any> {
    return this.http.get(API_URL + '/team-calendar-overview', {
      params: {
        start: params.start,
        end: params.end,
      },
    });
  }

  getReklaEvaluation(filters: any = {}): Observable<any> {
    return this.http.get(API_URL + '/rekla', { params: filters });
  }

  getReklaCausers(): Observable<any> {
    return this.http.get(API_URL + '/rekla-causers');
  }

  getAbrechnungUmsatz(params: {
    year: number;
    week: number;
    customer_branch_id?: number | null;
  }): Observable<any> {
    const httpParams: { [key: string]: string } = {
      year: String(params.year),
      week: String(params.week),
    };
    if (params.customer_branch_id != null) {
      httpParams.customer_branch_id = String(params.customer_branch_id);
    }
    return this.http.get(API_URL + '/abrechnung/umsatz', { params: httpParams });
  }

  getArchiveEmailConfig(): Observable<any> {
    return this.http.get(API_URL + '/archive-email/config');
  }

  saveArchiveEmailConfig(payload: any): Observable<any> {
    return this.http.put(API_URL + '/archive-email/config', payload);
  }

  setArchiveEmailActive(accountId: string): Observable<any> {
    return this.http.put(API_URL + '/archive-email/active', { account_id: accountId });
  }

  deleteArchiveEmailAccount(accountId: string): Observable<any> {
    return this.http.delete(API_URL + '/archive-email/accounts/' + encodeURIComponent(accountId));
  }

  testArchiveEmailConnection(payload?: any): Observable<any> {
    return this.http.post(API_URL + '/archive-email/test', payload || {});
  }

  searchArchiveEmail(payload: {
    subject?: string;
    account_id?: string;
    prefer_username?: string;
    limit?: number;
    max_images?: number;
    include_without_images?: boolean;
  }): Observable<any> {
    return this.http.post(API_URL + '/archive-email/search', payload);
  }

  recentArchiveEmails(payload?: { account_id?: string; limit?: number }): Observable<any> {
    return this.http.post(API_URL + '/archive-email/recent', payload || {});
  }

  getArchiveEmailMessage(payload: {
    uid?: string | number;
    msgn?: string | number;
    account_id?: string;
    prefer_username?: string;
    max_attachments?: number;
  }): Observable<any> {
    return this.http.post(API_URL + '/archive-email/message', payload);
  }

  syncArchiveEmailImages(payload?: {
    account_id?: string;
    limit?: number;
    max_images?: number;
    prefer_username?: string;
  }): Observable<any> {
    return this.http.post(API_URL + '/archive-email/sync', payload || {});
  }

  syncArchiveEmailPending(payload?: {
    account_id?: string;
    limit?: number;
    prefer_username?: string;
  }): Observable<any> {
    return this.http.post(API_URL + '/archive-email/sync/pending', payload || {});
  }

  syncArchiveEmailProcessOne(payload: {
    uid?: string | number | null;
    msgn?: string | number | null;
    account_id?: string;
    batch_id?: string;
    max_images?: number;
    prefer_username?: string;
  }): Observable<any> {
    return this.http.post(API_URL + '/archive-email/sync/process-one', payload);
  }

  getArchiveEmailSyncResults(params?: {
    limit?: number;
    status?: string;
    batch_id?: string;
  }): Observable<any> {
    const httpParams: { [key: string]: string } = {};
    if (params?.limit != null) {
      httpParams.limit = String(params.limit);
    }
    if (params?.status) {
      httpParams.status = params.status;
    }
    if (params?.batch_id) {
      httpParams.batch_id = params.batch_id;
    }
    return this.http.get(API_URL + '/archive-email/sync-results', {
      params: httpParams,
    });
  }

  downloadArchiveEmailSyncPdf(payload: {
    batch_id?: string;
    account?: string;
    rows?: any[];
    success_count?: number;
    skipped_count?: number;
    error_count?: number;
    images_total?: number;
  }): Observable<Blob> {
    return this.http.post(API_URL + '/archive-email/sync-results/pdf', payload, {
      responseType: 'blob',
    });
  }

  // --- PLZ-Gebiete ---

  getPlzGebiete(params?: { customer_branch_id?: number; q?: string }): Observable<any> {
    const httpParams: { [key: string]: string } = {};
    if (params?.customer_branch_id != null) {
      httpParams.customer_branch_id = String(params.customer_branch_id);
    }
    if (params?.q) {
      httpParams.q = params.q;
    }
    return this.http.get(API_URL + '/plz-gebiete', {
      params: Object.keys(httpParams).length ? httpParams : undefined,
    });
  }

  savePlzGebiet(payload: {
    id?: number | string;
    name: string;
    plz: string[];
    meta?: Record<string, any>;
    customer_branch_id?: number | null;
    color?: string;
  }): Observable<any> {
    const body = { data: payload };
    if (payload.id != null && String(payload.id).match(/^\d+$/)) {
      return this.http.put(API_URL + '/plz-gebiete/' + payload.id, body);
    }
    return this.http.post(API_URL + '/plz-gebiete', body);
  }

  deletePlzGebiet(id: number | string): Observable<any> {
    return this.http.delete(API_URL + '/plz-gebiete/' + id);
  }

  importLocalPlzGebiete(items: any[]): Observable<any> {
    return this.http.post(API_URL + '/plz-gebiete/import-local', { data: { items } });
  }

  generatePlzGebieteFromBranch(customerBranchId: number, radiusKm = 80): Observable<any> {
    return this.http.post(API_URL + '/plz-gebiete/generate-from-branch', {
      data: {
        customer_branch_id: customerBranchId,
        radius_km: radiusKm,
      },
    });
  }

  fetchPlzPolygonsBatch(plzList: string[]): Observable<any> {
    return this.http.post(API_URL + '/plz-gebiete/polygons', {
      data: { plz: plzList },
    });
  }

  storePlzGebietGeometries(
    gebietId: number | string,
    geometries: Record<string, any>
  ): Observable<any> {
    return this.http.post(API_URL + '/plz-gebiete/' + gebietId + '/geometries', {
      data: { geometries },
    });
  }

  getPlzGebietDayAssignments(params: {
    start: string;
    end: string;
    team_base_id?: number | number[];
  }): Observable<any> {
    let httpParams = new HttpParams().set('start', params.start).set('end', params.end);
    if (params.team_base_id != null) {
      const ids = Array.isArray(params.team_base_id) ? params.team_base_id : [params.team_base_id];
      ids.forEach((id) => {
        httpParams = httpParams.append('team_base_id[]', String(id));
      });
    }
    return this.http.get(API_URL + '/plz-gebiete/day-assignments', { params: httpParams });
  }

  assignPlzGebietToDay(payload: {
    team_base_id: number;
    plz_gebiet_id: number;
    assignment_date: string;
  }): Observable<any> {
    return this.http.post(API_URL + '/plz-gebiete/day-assignments', { data: payload });
  }

  removePlzGebietDayAssignment(id: number): Observable<any> {
    return this.http.delete(API_URL + '/plz-gebiete/day-assignments/' + id);
  }

  getPlzGebieteForTeam(teamBaseId: number, customerBranchId?: number | null): Observable<any> {
    const httpParams: { [key: string]: string } = {};
    if (customerBranchId != null) {
      httpParams.customer_branch_id = String(customerBranchId);
    }
    return this.http.get(API_URL + '/plz-gebiete/for-team/' + teamBaseId, {
      params: Object.keys(httpParams).length ? httpParams : undefined,
    });
  }

  suggestPlzGebietDays(params: {
    order_id?: number;
    postal_code?: string;
    customer_branch_id?: number;
    from?: string;
    to?: string;
  }): Observable<any> {
    const httpParams: { [key: string]: string } = {};
    if (params.order_id != null) httpParams.order_id = String(params.order_id);
    if (params.postal_code) httpParams.postal_code = params.postal_code;
    if (params.customer_branch_id != null) {
      httpParams.customer_branch_id = String(params.customer_branch_id);
    }
    if (params.from) httpParams.from = params.from;
    if (params.to) httpParams.to = params.to;
    return this.http.get(API_URL + '/plz-gebiete/suggest-days', { params: httpParams });
  }

  // --- WhatsApp Business ---

  getWhatsAppConfig(): Observable<any> {
    return this.http.get(API_URL + '/whatsapp/config');
  }

  saveWhatsAppConfig(payload: any): Observable<any> {
    return this.http.put(API_URL + '/whatsapp/config', payload);
  }

  setWhatsAppActive(accountId: string): Observable<any> {
    return this.http.put(API_URL + '/whatsapp/active', { account_id: accountId });
  }

  deleteWhatsAppAccount(accountId: string): Observable<any> {
    return this.http.delete(API_URL + '/whatsapp/accounts/' + encodeURIComponent(accountId));
  }

  testWhatsAppConnection(payload?: any): Observable<any> {
    return this.http.post(API_URL + '/whatsapp/test', payload || {});
  }

  sendWhatsAppTestMessage(payload: {
    to: string;
    message?: string;
    template_name?: string;
    template_language?: string;
    account_id?: string;
  }): Observable<any> {
    return this.http.post(API_URL + '/whatsapp/send-test', payload);
  }

  getWhatsAppTemplates(params?: { account_id?: string }): Observable<any> {
    const httpParams: { [key: string]: string } = {};
    if (params?.account_id) {
      httpParams.account_id = params.account_id;
    }
    return this.http.get(API_URL + '/whatsapp/templates', {
      params: Object.keys(httpParams).length ? httpParams : undefined,
    });
  }

  /** Dispo-Postfach (IMAP/Graph aus API-.env). */
  getDispoMail(params?: { limit?: number; mailbox?: string }): Observable<any> {
    const httpParams: { [key: string]: string } = {};
    if (params?.limit != null) {
      httpParams.limit = String(params.limit);
    }
    if (params?.mailbox) {
      httpParams.mailbox = params.mailbox;
    }
    return this.http.get(API_URL + '/dispo-mail', {
      params: Object.keys(httpParams).length ? httpParams : undefined,
    });
  }

  getDispoMailMessage(params: { id: string; mailbox?: string }): Observable<any> {
    const httpParams: { [key: string]: string } = {
      id: params.id,
    };
    if (params.mailbox) {
      httpParams.mailbox = params.mailbox;
    }
    return this.http.get(API_URL + '/dispo-mail/message', { params: httpParams });
  }

  // --- SIP GUN ---

  getSipGunStatus(): Observable<any> {
    return this.http.get(API_URL + '/sip-gun/status');
  }

  sipGunRing(payload: {
    number: string;
    ring_seconds?: number;
    device_id?: string;
    caller_id?: string;
    mode?: 'system' | 'classic';
  }): Observable<any> {
    return this.http.post(API_URL + '/sip-gun/ring', payload);
  }

  sipGunSpeak(payload: {
    number: string;
    text: string;
    device_id?: string;
    caller_id?: string;
    voice_id?: string;
    ring_seconds?: number;
    connect_wait_seconds?: number;
  }): Observable<any> {
    return this.http.post(API_URL + '/sip-gun/speak', payload);
  }

}
